import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { TextInputFormField } from 'packages/common-frontend/components/FormField';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { GET_CREDIT_FACILITIES } from '~/packages/credit-facility/requests/requests.gql';
import { useUpdateCreditFacilityNameMutation } from '~/packages/credit-facility/requests/requests.gql.generated';
import { GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS } from '~/packages/implementations/requests/implementations.gql';
import { GetOrganizationImplementationsDetailsQueryGQLType } from '~/packages/implementations/requests/implementations.gql.generated';

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

const editValidationSchema = Yup.object().shape({
  name: Yup.string().min(5).trim().strict().required(),
});

// Exports
type Props = {
  implementation: OrganizationImplementationFragmentGQLType;
  creditFacility: { id: string; name: string; isActive: boolean };
};
export const UpdateCreditFacilityNameButton = ({
  implementation,
  creditFacility,
}: Props) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [
    updateCreditFacilityName,
    updateCreditFacilityNameMutation,
  ] = useUpdateCreditFacilityNameMutation({
    refetchQueries: [
      {
        query: GET_CREDIT_FACILITIES,
        variables: { organizationId: implementation.borrowerOrganization.id },
      },
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS,
        variables: { organizationId: implementation.ownerOrganization.id },
      },
    ],
    awaitRefetchQueries: true,
  });
  return (
    <>
      <IconButtonV2
        aria-label="Edit name"
        icon={<Icon icon="gear" />}
        label="Edit name"
        onClick={() => setIsOpen(true)}
        variant="v2-ghost"
        colorScheme="gray"
        placement="bottom"
        isDisabled={!creditFacility.isActive}
      />

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent padding="20px">
          <ModalHeader>Edit credit facility name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik
              onSubmit={async (values) => {
                try {
                  await updateCreditFacilityName({
                    variables: {
                      creditFacilityId: creditFacility.id,
                      name: values.name,
                    },
                  });
                  toast({
                    ...successToastConfig,
                    title: `Successfully renamed credit facility to "${values.name}"`,
                  });
                  setIsOpen(false);
                } catch (err: any) {
                  toast({
                    ...errorToastConfig,
                    title: `Failed to rename credit facility to "${values.name}": ${err}`,
                  });
                }
              }}
              validationSchema={editValidationSchema}
              initialValues={{ name: creditFacility.name }}
              validateOnChange={false}
              validateOnBlur
            >
              {(formik) => (
                <Form onSubmit={formik.handleSubmit}>
                  <VStack spacing="10px">
                    <TextInputFormField label="Facility Name" name="name" />
                    <Flex padding-top="5px" justifyContent="center">
                      <Button
                        isLoading={updateCreditFacilityNameMutation.loading}
                        type="submit"
                      >
                        Save
                      </Button>
                    </Flex>
                  </VStack>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
